import { IonItem, IonList, IonListHeader, IonToolbar } from '@ionic/react';
import { isEmpty, isNil, merge } from 'lodash';
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import * as yup from 'yup';
import { usePermissions } from '../../../../../api/authorization.provider';
import {
  ScratchpadBucket,
  ScratchpadBucketScope,
  ScratchpadBucketType,
  useGetScratchpadBucketQuery,
} from '../../../../../api/graphql/generated';
import { ActionButton } from '../../../../../components/button/action-button';
import { RightAlignedButtons } from '../../../../../components/button/aligned-buttons';
import { useScratchpadServiceContext } from '../../../../../components/machine/scratchpad/scratchpad-service-context-provider';
import { SaveStatusToast } from '../../../../../components/toast/toast.gql';
import { trackScratchpadRecommendationSaved } from '../../../../../config/analytics';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { useGetFlowRecommendation } from '../../../beacon/cutting/component/settings/recommendations/settings-recommendation.utils';
import {
  ApplicationDefaultParameterSetFormikFormValues,
  useApplicationDefaultParameterSetService,
  useGetApplicationDefaultParameterSetTags,
} from '../../hook/application-default-parameter-set.hooks';
import { useFlowServices } from '../../hook/flow-service.hooks';
import {
  ApplicationDefaultParameterSet,
  ApplicationDefaultParameterSetBaseFormChild,
  ApplicationDefaultParameterSetBaseFormValidationBuilder,
  Flow,
} from '../../model/flow.types';
import { useFlowRefContext } from '../flow/flow-ref';
import {
  ApplicationSettingsBucketEditModal,
  ApplicationSettingsBucketEditModalProps,
} from './application-settings-bucket-edit-modal';
import { ApplicationSettingsBucketPicker } from './application-settings-bucket-picker';

export type ApplicationDefaultParameterSetLocalFormikFormValues = Pick<
  ApplicationDefaultParameterSetFormikFormValues,
  'tags' | 'data'
>;

interface ApplicationSettingsEditProps {
  afterSave?: (parameterSet: ApplicationDefaultParameterSet) => void;
  readOnly?: boolean;
  showBucketExplorer?: (show: boolean) => void;
  presentingElement?: HTMLElement;
  type: ScratchpadBucketType;
}

enum SaveAction {
  LocalOnly,
  LocalAndUpdate,
  LocalAndCreateNew,
}

const ApplicationSettingsEdit = (
  props: ApplicationSettingsEditProps
): ReactElement => {
  const {
    flowService,
    flowAnalyticService,
    flowItemService,
    saveFlow,
    flow,
    masterApplicationDefaultParameterSet,
    system,
  } = useFlowServices();

  const {
    componentRegistry: { ApplicationSettingsForm },
  } = useFlowRefContext();

  const { afterSave, readOnly, showBucketExplorer, presentingElement, type } =
    props;
  const { scratchpad, save: saveScratchpad } = useScratchpadServiceContext();

  const {
    update: [
      updateApplicationDefaultParameterSettings,
      { updateError, updateLoading },
    ],
  } = useApplicationDefaultParameterSetService();

  const { data: scratchpadBucketData } = useGetScratchpadBucketQuery({
    variables: {
      input: {
        id: scratchpad.bucket1Id as string,
      },
    },
    skip: !scratchpad.bucket1Id,
  });

  const [saving, setSaving] = useState<boolean>(false);
  const saveActionRef = useRef<SaveAction | null>(null);
  const formValues: ApplicationDefaultParameterSetLocalFormikFormValues =
    useMemo(() => {
      return {
        data: flowItemService.convertFlowItemToForm(
          flowItemService.getDefaultItemForRef(
            flowItemService.getDefaultItemRef()
          ),
          system
        ),
        tags: flowService.mapFlowConfigToApplicationDefaultParameterSetFormTags(
          flow
        ),
      };
    }, [flow, flowService, flowItemService, system]);

  const save = useCallback(
    async (parameterSet: ApplicationDefaultParameterSet, flow: Flow) => {
      const mappedParameterSet = merge(parameterSet, {
        data: {
          parameters: parameterSet.data.parameters.map((p) => ({
            ...p,
            baseValue: p.value,
          })),
        },
      });

      await saveFlow(
        { ...flow, defaultParameterSet: mappedParameterSet },
        { saveAsDelta: true }
      );

      if (typeof afterSave === 'function') {
        afterSave(parameterSet);
      }
    },
    [afterSave, saveFlow]
  );

  const [modalProps, setModalProps] = useState<
    Omit<ApplicationSettingsBucketEditModalProps, 'isOpen'> | undefined
  >();

  const [modalOpen, setModalOpen] = useState(false);

  const scratchpadPermissions = usePermissions('scratchpad', scratchpad);

  const bucketPermissions = usePermissions<Partial<ScratchpadBucket>>(
    'scratchpad_bucket',
    masterApplicationDefaultParameterSet,
    {
      createObj: {
        parentId: scratchpad?.machineId,
        scope: ScratchpadBucketScope.MACHINE,
      },
    }
  );

  const getFlowRecommendation = useGetFlowRecommendation();

  const onSubmit = useCallback(
    async (
      form: ApplicationDefaultParameterSetLocalFormikFormValues,
      recommendationId?: string
    ) => {
      const { data, tags } = form;

      setSaving(true);

      const item = flowItemService.convertFormToFlowItem(data, system);
      const updatedFlow =
        flowService.mapApplicationDefaultParameterSetTagsToFlowConfig(
          flow,
          tags
        );

      const flowRecommendation = await getFlowRecommendation({
        recommendationId,
      });

      if (saveActionRef.current === SaveAction.LocalOnly) {
        const applicationDefaultParameterSet: ApplicationDefaultParameterSet = {
          ...flow.defaultParameterSet,
          data: {
            parameters: item.parameters,
          },
        };
        await save(applicationDefaultParameterSet, {
          ...flow,
          config: updatedFlow.config,
          recommendation: flowRecommendation,
        });

        flowAnalyticService.trackAppliedSettingsToPart(
          scratchpad.bucket1Id !== undefined
        );
      } else if (saveActionRef.current === SaveAction.LocalAndUpdate) {
        const { data: updatedBucketData } =
          await updateApplicationDefaultParameterSettings({
            id: String(scratchpad.bucket1Id),
            data: {
              parameters: item.parameters,
            },
            tags: {
              ...scratchpadBucketData?.getScratchpadBucket.tags,
              ...tags,
            },
          });
        const applicationDefaultParameterSet: ApplicationDefaultParameterSet = {
          ...flow.defaultParameterSet,
          version: String(updatedBucketData?.updateScratchpadBucket.version),
          data: {
            parameters: item.parameters,
          },
        };
        await save(applicationDefaultParameterSet, {
          ...flow,
          config: updatedFlow.config,
          recommendation: flowRecommendation,
        });

        flowAnalyticService.trackAppliedSettingsToPartAndUpdated();
      } else if (saveActionRef.current === SaveAction.LocalAndCreateNew) {
        setModalProps({
          system,
          onClose: () => {
            setModalOpen(false);
          },
          onSave: async (savedBucket) => {
            setSaving(true);

            let parameterSet: ApplicationDefaultParameterSet | undefined =
              undefined;

            if (savedBucket) {
              parameterSet = {
                id: savedBucket.id,
                version: String(savedBucket.version),
                data: savedBucket.data,
              };

              let flow: Flow = {
                ...scratchpad.appData,
                defaultParameterSet: parameterSet,
              };

              flow =
                flowService.mapApplicationDefaultParameterSetTagsToFlowConfig(
                  flow,
                  savedBucket.tags
                );

              await saveScratchpad(
                {
                  ...scratchpad,
                  bucket1Id: savedBucket.id,
                  appData: {
                    ...flow,
                    recommendation: flowRecommendation,
                  },
                },
                { saveAsDelta: true }
              );
              setSaving(false);
            }

            if (flowRecommendation) {
              trackScratchpadRecommendationSaved(flowRecommendation.utilized);
            }

            setModalOpen(false);

            if (typeof afterSave === 'function' && parameterSet) {
              afterSave(parameterSet);
            }
          },
          bucketData: {
            parameters: item.parameters,
          },
          sourceBucketId: masterApplicationDefaultParameterSet?.id,
          bucketId: undefined,
          applicationParametersReadOnly: true,
          tags: { ...masterApplicationDefaultParameterSet?.tags, ...tags },
          presentingElement,
          type,
        });
        flowAnalyticService.trackAppliedSettingsToPartAndCreated();
        setModalOpen(true);
      }

      setSaving(false);
    },
    [
      flowItemService,
      system,
      flowService,
      flow,
      getFlowRecommendation,
      save,
      flowAnalyticService,
      scratchpad,
      updateApplicationDefaultParameterSettings,
      scratchpadBucketData?.getScratchpadBucket.tags,
      masterApplicationDefaultParameterSet?.id,
      masterApplicationDefaultParameterSet?.tags,
      presentingElement,
      afterSave,
      saveScratchpad,
      type,
    ]
  );

  const showSave = scratchpad.id
    ? scratchpadPermissions.update
    : scratchpadPermissions.create;

  const onUnlinkSet = useCallback(async () => {
    if (!scratchpad.bucket1Id) {
      return;
    }

    const existingAppData: Flow = scratchpad.appData;

    const appData: Flow = {
      ...scratchpad.appData,
      defaultParameterSet: {
        ...existingAppData.defaultParameterSet,
        id: undefined,
        version: undefined,
      },
    };
    await saveScratchpad({ ...scratchpad, appData, bucket1Id: null });
  }, [saveScratchpad, scratchpad]);

  const tags = useGetApplicationDefaultParameterSetTags();

  const buildValidationSchema: ApplicationDefaultParameterSetBaseFormValidationBuilder =
    useCallback(
      (dataSchema) => {
        const tagConfig =
          flowService.getApplicationDefaultParameterSetTagToConfig();

        const tagsSchema: yup.AnySchema = Object.entries(tags).reduce(
          (schema, [k, v]) => {
            let tagSchema: yup.AnySchema = yup.string();

            if (v.required && k in tagConfig) {
              tagSchema = tagSchema.required();
            }

            schema = schema.shape({
              [k]: tagSchema,
            });

            return schema;
          },
          yup.object().shape({})
        );

        const schemaShape: Record<string, yup.AnySchema> = {
          data: dataSchema ?? yup.object().required(),
        };

        schemaShape.tags = tagsSchema;

        const itemSchema = yup.object().shape(schemaShape);

        return itemSchema;
      },
      [tags, flowService]
    );

  const footer: ApplicationDefaultParameterSetBaseFormChild<
    ApplicationDefaultParameterSetLocalFormikFormValues
  > = ({ formikProps: { errors, dirty, submitForm }, disabled }) => {
    const isReadyToSubmit = isEmpty(errors);
    const hasNeverBeenSaved = !flow.defaultParameterSet;

    return (
      <IonToolbar>
        {showSave && (
          <RightAlignedButtons>
            <ActionButton
              label={getLocalizedText('common:save_options')}
              loading={saving}
              buttonProps={{
                color: isReadyToSubmit ? 'primary' : 'medium',
                disabled: !isReadyToSubmit || saving || readOnly || disabled,
                fill: isReadyToSubmit ? 'solid' : 'outline',
              }}
            >
              {({ close }) => {
                return (
                  <IonList lines="full">
                    <IonItem
                      disabled={!dirty && !hasNeverBeenSaved}
                      onClick={(e) => {
                        e.preventDefault();
                        saveActionRef.current = SaveAction.LocalOnly;
                        submitForm();
                        close();
                      }}
                      href="#"
                      detail={false}
                    >
                      {getLocalizedText('common:save_to_part')}
                    </IonItem>
                    <IonItem
                      disabled={
                        isNil(scratchpad.bucket1Id) || !bucketPermissions.update
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        saveActionRef.current = SaveAction.LocalAndUpdate;
                        submitForm();
                        close();
                      }}
                      href="#"
                      detail={false}
                    >
                      {getLocalizedText(
                        'common:save_to_part_and_update_library'
                      )}
                    </IonItem>
                    <IonItem
                      lines="none"
                      disabled={!bucketPermissions.create}
                      onClick={(e) => {
                        e.preventDefault();
                        saveActionRef.current = SaveAction.LocalAndCreateNew;
                        submitForm();
                        close();
                      }}
                      href="#"
                      detail={false}
                    >
                      {getLocalizedText(
                        'common:save_to_part_and_create_library'
                      )}
                    </IonItem>
                  </IonList>
                );
              }}
            </ActionButton>
          </RightAlignedButtons>
        )}
      </IonToolbar>
    );
  };

  const pre: ApplicationDefaultParameterSetBaseFormChild<ApplicationDefaultParameterSetLocalFormikFormValues> =
    useCallback(() => {
      return (
        <>
          <div className="configuration-header">
            <IonListHeader>
              {getLocalizedText('common:library_header')}
            </IonListHeader>
          </div>
          <ApplicationSettingsBucketPicker
            onOpenSearch={() => {
              if (showBucketExplorer) {
                showBucketExplorer(true);
                flowAnalyticService.trackLaunchedBucketExplorer();
              }
            }}
            onUnlinkSet={onUnlinkSet}
          />
          <div style={{ height: 20 }} />
        </>
      );
    }, [onUnlinkSet, showBucketExplorer, flowAnalyticService]);

  return (
    <>
      <ApplicationSettingsForm
        buildValidationSchema={buildValidationSchema}
        footer={footer}
        formValues={formValues}
        onSubmit={onSubmit}
        preForm={pre}
        tagsReadOnly={readOnly}
        paramsReadOnly={readOnly}
        system={system}
      />
      {modalProps && (
        <ApplicationSettingsBucketEditModal
          {...modalProps}
          isOpen={modalOpen}
        />
      )}
      <SaveStatusToast loading={updateLoading} error={updateError} />
    </>
  );
};

export { ApplicationSettingsEdit };
